import { EXPERIENCE_PERMALINK_DOMAIN, MEDIAPLAYER_URL } from "../../constants";
import { countries } from "countries-list";
import { memoize } from "lodash";

export const getStatusColor = (status?: string) => {
  switch (status) {
    case "Error":
      return "error";
    case "Generating":
      return "processing";
    case "Cancelled":
      return "warning";
    case "Ready":
      return "success";
    default:
      return "default";
  }
};

// https://stackoverflow.com/questions/1322732/convert-seconds-to-hh-mm-ss-with-javascript
export const secToHHMMSS = (sec: number) => {
  const min = Math.floor(sec / 60);
  const secs = sec % 60;
  return (min < 10 ? "0" : "") + min + ":" + (secs < 10 ? "0" : "") + secs;
};

// The contractions don't count as separate words
// both the straight one (') and the curly one (’) are handled properly
// "entry-level" counts as two words, which makes sense with regards to spoken words, and Wiley's counts as only one
export function countWords(str: string) {
  // This works across all writing systems - English, Russian, Arabic, Chinese, etc.
  //  Word boundaries are a Latin script limitation. Drop them for international text support!
  const words = str.match(/[\p{L}\p{M}\p{N}'\u2019_]+/gu);
  return words ? words.length : 0;
}

export const guessProjectTitleFromDocument = (filenames: string[]): string => {
  const filename = filenames[0] ?? "";
  const filenameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
  // remove some chars
  return filenameWithoutExt
    .replaceAll(/[._,-]/g, " ") // replace dots, underscores, commas, hyphens with spaces
    .replaceAll(/\(\d+\)/g, "") // remove (1), (2), etc.
    .trim();
};

export const guessProjectTitleFromUrl = (url: string) => {
  try {
    // Truncate URL to 90 characters and append .html
    const truncatedUrl = url.length > 90 ? url.substring(0, 90) : url;
    return `${truncatedUrl}.html`;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "Unknown.html";
  }
};

export function transformKeysToCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToCamelCase(item));
  } else if (obj !== null && obj?.constructor === Object) {
    const newObj: { [key: string]: any } = {};
    Object.keys(obj).forEach((key) => {
      const camelCaseKey = key.replace(/(_\w)/g, (match) =>
        match.charAt(1).toUpperCase()
      );
      newObj[camelCaseKey] = transformKeysToCamelCase(obj[key]);
    });
    return newObj;
  }
  return obj;
}

export function transformKeysToSnakeCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformKeysToSnakeCase(item));
  } else if (obj !== null && obj.constructor === Object) {
    const newObj: { [key: string]: any } = {};
    Object.keys(obj).forEach((key) => {
      const snakeCaseKey = key.replace(
        /[A-Z]/g,
        (match) => "_" + match.toLowerCase()
      );
      newObj[snakeCaseKey] = transformKeysToSnakeCase(obj[key]);
    });
    return newObj;
  }
  return obj;
}

export const getLocaleCountryCode = (locale: string | undefined) => {
  // don't fail if empty language
  if (locale === "yue") return "hk";
  return locale?.split("-").pop()?.toLowerCase();
};

export const normalizeLocale = (locale: string) => {
  if (locale?.length === 2) {
    if (locale === "en") {
      return "en-US";
    }
    // naive implementation in case we get a 2 letter locale like fr
    return `${locale}-${locale.toUpperCase()}`;
  } else return locale;
};

export const addDateTz = (date: string | undefined) => {
  if (date === undefined || date === null) return date;
  return date?.endsWith("Z") ? date : date + "Z";
};

export const validateSelectedPages = (input: string): string | false => {
  // Regular expression to match numbers and ranges
  const pattern = /^(\d+(-\d+)?)(,\s*\d+(-\d+)?)*$/;
  if (input === "") {
    return "";
  }
  if (!pattern.test(input)) {
    return false;
  }

  const elements: string[] = input.split(/,\s*/);

  const formattedParts: string[] = [];
  for (const element of elements) {
    if (element.includes("-")) {
      const [start, end] = element.split("-").map(Number);
      if (
        start === undefined ||
        end === undefined ||
        Number.isNaN(start) ||
        Number.isNaN(end) ||
        start > end
      ) {
        return false;
      }
      formattedParts.push(`${start}-${end}`);
    } else {
      const num: number = Number.parseInt(element, 10);
      if (Number.isNaN(num)) {
        return false;
      }
      formattedParts.push(num.toString());
    }
  }

  return formattedParts.join(", ");
};

export const getPublicationPermalinkPrefix = () => {
  return `${EXPERIENCE_PERMALINK_DOMAIN}/share`;
};
// Appends the optional publication query params to a base URL. Shared by
// both getPublicationUrl branches so the param logic lives in one place
// (also keeps getPublicationUrl's cognitive complexity down). accessToken
// is ignored on the slug branch since it's only reached when there is none.
function appendPublicationParams(
  baseUrl: string,
  {
    accessToken,
    isDocHeyGenius,
    isAudioOnly,
  }: { accessToken?: string; isDocHeyGenius?: boolean; isAudioOnly?: boolean },
): string {
  const params = new URLSearchParams();
  if (accessToken) params.set("access_token", accessToken);
  if (isDocHeyGenius) params.set("hey_genius", "doc");
  if (isAudioOnly) params.set("audio_only", "true");
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

export function getPublicationUrl({
  projectId,
  publicationId,
  embed = false,
  accessToken,
  instanceSlug,
  organizationSlug,
  isDocHeyGenius = false,
  isAudioOnly = false,
  experienceV2 = false,
}: {
  projectId: string;
  publicationId: string;
  embed?: boolean;
  accessToken?: string;
  instanceSlug?: string;
  organizationSlug?: string;
  isDocHeyGenius?: boolean;
  isAudioOnly?: boolean;
  /**
   * Target the NEW Knowledge Hub experience route (document + Knowledge Hub
   * + chat) instead of the legacy player. Same slug-vs-id selection, with an
   * `/experience` path prefix. This is the surface a SCORM package launches.
   */
  experienceV2?: boolean;
}) {
  const params = { accessToken, isDocHeyGenius, isAudioOnly };

  // we might have a slug set with an access token after an unpublish
  if (instanceSlug && organizationSlug && !accessToken) {
    const baseUrl = experienceV2
      ? `${EXPERIENCE_PERMALINK_DOMAIN}/experience/share/${organizationSlug}/${instanceSlug}`
      : `${getPublicationPermalinkPrefix()}/${organizationSlug}/${instanceSlug}`;
    return appendPublicationParams(baseUrl, params);
  }

  const embedOrP = embed ? "embed" : "p";
  const baseUrl = experienceV2
    ? `${MEDIAPLAYER_URL}/experience/${projectId}/${publicationId}`
    : `${MEDIAPLAYER_URL}/${embedOrP}/${projectId}/${publicationId}`;
  return appendPublicationParams(baseUrl, params);
}
export const getCountryOptions = () => {
  return Object.entries(countries).map(([key, value]) => ({
    label: value.name,
    value: key,
  }));
};
export const getFileExtension = (url: string): string => {
  const parts = url.split(".");
  const lastPart = parts[parts.length - 1] ?? "";
  const extension = lastPart.split("?")[0] ?? "";
  return extension.toLowerCase();
};

export const getPrettyFontName = memoize(
  (fullName: string, fallback = "Example") => {
    const name = fullName.trim();
    if (!name) return fallback;
    const fileExtensionIndex = name.lastIndexOf(".");
    const hasFileExtension =
      fileExtensionIndex > 0 && fileExtensionIndex <= name.length - 3;
    const filenameWithoutExt = hasFileExtension
      ? name.substring(0, fileExtensionIndex)
      : name;
    const parts = filenameWithoutExt.split(/[-_]/);

    // If there's only one part, return it as is
    if (parts.length <= 1) {
      return filenameWithoutExt;
    }

    // Check if the last part is a font type (weight/style)
    const fontTypes = [
      "regular",
      "normal",
      "light",
      "thin",
      "ultralight",
      "extralight",
      "bold",
      "semibold",
      "medium",
      "demibold",
      "heavy",
      "black",
      "extrabold",
      "italic",
      "oblique",
      "condensed",
      "expanded",
      "narrow",
      "wide",
    ];

    const fontTypeIndex = parts.findIndex((part) =>
      fontTypes.includes(part.toLowerCase())
    );
    const hasFontType = fontTypeIndex > 0;

    if (hasFontType) {
      return parts.slice(0, fontTypeIndex).join(" ");
    } else {
      return parts[0];
    }
  },
  (fullName, fallback) => `${fullName}:${fallback}`
);

// Normalize a user-entered value to a URL the player can open directly.
export const normalizeActionUrl = (type: string, url: string): string => {
  const trimmed = url.trim();
  if (!trimmed || /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) return trimmed;
  if (type === "Email" || type === "Phone") return trimmed;
  return `https://${trimmed}`;
};

export const hasLabelAndValue = (label: string, value: string): boolean =>
  label.trim().length > 0 && value.trim().length > 0;
